import React from 'react'
import {useTranslation} from 'react-i18next'
import {
  Row,
  Col,
  ListGroup,
  ListGroupItem
} from 'reactstrap'
import styled from 'styled-components'
import parse from 'react-html-parser'

const DetailItem = styled(ListGroupItem).attrs({ className: 'row' })`
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
`

const Accessibility = (props) => {
  const { i18n } = useTranslation()
  const { accessibility_grade, accessibility_grade_label, accessibility_description } = props

  if (accessibility_grade === 'accessibility_grade_unkown') return null

  return (
    <DetailItem>
      <Row>
        <Col xs="12" sm="4" className="fw-bold">
          <i className="fa fa-wheelchair me-1" /> {i18n.t('product_info.accessibility_title')}
        </Col>
        {accessibility_grade && (
          <Col xs="12" sm="8" className="mt-2 mt-sm-0">
            <span className="fw-semibold">{accessibility_grade_label}</span>
            {accessibility_description && (
              <>
                <br />
                {accessibility_description}
              </>
            )}
          </Col>
        )}
      </Row>
    </DetailItem>
  )
}

const Languages = ({ languages_label }) => {
  const { i18n } = useTranslation()

  return (
    <DetailItem>
      <Row>
        <Col xs="12" sm="4" className="fw-bold">
          <i className="fa fa-language me-1" /> {i18n.t('product_info.languages_title')}
        </Col>
        <Col xs="12" sm="8" className="mt-2 mt-sm-0">
          {languages_label}
        </Col>
      </Row>
    </DetailItem>
  )
}

const PetFriendly = (props) => {
  const { i18n } = useTranslation()
  const { pet_friendly_grade, pet_friendly_grade_label, pet_friendly_description } = props

  if (pet_friendly_grade === 'pet_friendly_grade_unkown') return null

  return (
    <DetailItem>
      <Row>
        <Col xs="12" sm="4" className="fw-bold">
          <i className="fas fa-paw me-1" /> {i18n.t('product_info.pet_friendly_title')}
        </Col>
        {pet_friendly_grade && (
          <Col xs="12" sm="8" className="mt-2 mt-sm-0">
            <span className="fw-semibold">{pet_friendly_grade_label}</span>
            {pet_friendly_description && (
              <>
                <br />
                {pet_friendly_description}
              </>
            )}
          </Col>
        )}
      </Row>
    </DetailItem>
  )
}

const EVoucher = ({ evoucher }) => {
  const { i18n } = useTranslation()

  return (
    <DetailItem>
      <Row>
        <Col xs="12" sm="4" className="fw-bold">
          <i className="fa fa-mobile-alt me-2" />
          {evoucher
            ? i18n.t('product_info.evoucher_allowed_title')
            : i18n.t('product_info.evoucher_not_allowed_title')}
        </Col>
        <Col xs="12" sm="8" className="mt-2 mt-sm-0">
          {evoucher
            ? i18n.t('product_info.evoucher_allowed')
            : i18n.t('product_info.evoucher_not_allowed')}
        </Col>
      </Row>
    </DetailItem>
  )
}

const Includes = ({ includes }) => {
  const { i18n } = useTranslation()
  const includesArray = JSON.parse(includes)

  if (!includesArray || includesArray.length < 1) return null

  return (
    <DetailItem>
      <Row>
        <Col xs="12" sm="4" className="fw-bold">
          <i className="fa fa-check me-2" /> {i18n.t('product_info.includes_title')}
        </Col>
        <Col xs="12" sm="8" className="mt-2 mt-sm-0">
          <ul className="m-0 p-0 list-unstyled">
            {includesArray.map((item, index) => (
              <li key={index}>
                <i className="fa fa-check text-success me-2" /> {parse(item)}
              </li>
            ))}
          </ul>
        </Col>
      </Row>
    </DetailItem>
  )
}

const NotIncludes = ({ not_includes }) => {
  const { i18n } = useTranslation()
  const notIncludesArray = JSON.parse(not_includes)

  if (!notIncludesArray || notIncludesArray.length < 1) return null

  return (
    <DetailItem>
      <Row>
        <Col xs="12" sm="4" className="fw-bold">
          <i className="fa fa-times me-2" /> {i18n.t('product_info.not_includes_title')}
        </Col>
        <Col xs="12" sm="8" className="mt-2 mt-sm-0">
          <ul className="m-0 p-0 list-unstyled">
            {notIncludesArray.map((item, index) => (
              <li key={index}>
                <i className="fa fa-times text-danger me-2" /> {parse(item)}
              </li>
            ))}
          </ul>
        </Col>
      </Row>
    </DetailItem>
  )
}

const MustKnow = ({ must_know }) => {
  const { i18n } = useTranslation()
  const mustKnowArray = JSON.parse(must_know)

  if (!mustKnowArray || mustKnowArray.length < 1) return null

  return (
    <DetailItem>
      <Row>
        <Col xs="12" sm="4" className="fw-bold">
          <i className="fa fa-info-circle me-2" /> {i18n.t('product_info.must_know')}
        </Col>
        <Col xs="12" sm="8" className="mt-2 mt-sm-0">
          <ul className="m-0 ps-3">
            {mustKnowArray.map((item, index) => (
              <li key={index}>{parse(item)}</li>
            ))}
          </ul>
        </Col>
      </Row>
    </DetailItem>
  )
}

const Features = (props) => {
  const { i18n } = useTranslation()
  const {
    duration,
    duration_description,
    languages_label,
    evoucher,
    accessibility_grade,
    pet_friendly_grade,
    includes,
    not_includes,
    must_know
  } = props

  return (
    <>
      <h2>{i18n.t('product_info.details_title')}</h2>

      <ListGroup className="mt-3 mx-3">
        {(duration || duration_description) && (
          <DetailItem style={{ borderTop: '1px solid rgba(0, 0, 0, 0.125)' }}>
            <Row>
              <Col xs="12" sm="4" className="fw-bold">
                <i className="fa fa-hourglass me-2" /> {i18n.t('product_info.duration_title')}
              </Col>
              <Col xs="12" sm="8" className="mt-2 mt-sm-0">
                {duration_description || duration}
              </Col>
            </Row>
          </DetailItem>
        )}

        {languages_label && <Languages languages_label={languages_label} />}
        {accessibility_grade && <Accessibility {...props} />}
        {pet_friendly_grade && <PetFriendly {...props} />}
        <EVoucher evoucher={evoucher} />
        <Includes includes={includes} />
        <NotIncludes not_includes={not_includes} />
        <MustKnow must_know={must_know} />
      </ListGroup>
    </>
  )
}

export default Features
